// Global Types
import type {
  $FullRegion,
  $Model,
} from 'common.data';

// Helpers
import _ from 'lodash';
import moment from 'moment';

// Enums
import * as mainActionTypes from './types';

// Types
import type {
  $ActionEnvelope,
  $ReducerActions,
} from '../..';
import type {
  $Menu,
} from '../../../routes/menu';

export type $State = {
  activeRegattaId: null | string;
  activeRegionId: null | string;
  activeRegionSeasonId: null | string;
  error: null | string;
  loading: boolean;
  menu: $Menu;
  regattas: Array<$Model<'Regatta'>>;
  regions: Array<$FullRegion>;
  showRegionSelector: boolean;
  start: moment.Moment | null;
  timezone: string;
};

export const initialState: $State = {
  activeRegattaId: null,
  activeRegionId: null,
  activeRegionSeasonId: null,
  error: null,
  loading: false,
  menu: [],
  regattas: [],
  regions: [],
  showRegionSelector: false,
  start: null,
  timezone: 'UTC',
};

const actionStart = (state: $State): $State => ({
  ...state,
  error: null,
  loading: false,
});

const actionEnd = (state: $State): $State => ({
  ...state,
  loading: false,
});

const actionError = (state: $State, payload: string): $State => ({
  ...state,
  error: payload,
  loading: false,
});

const actionSetActiveRegionId = (state: $State, payload: string): $State => ({
  ...state,
  activeRegionId: payload || null,
  loading: false,
  timezone: _.get(
    _.find(
      state.regions,
      {
        id: payload,
      },
    ),
    'Timezone',
    'UTC',
  ),
});

const actionSetActiveRegionSeasonId = (
  state: $State,
  payload?: string,
): $State => ({
  ...state,
  activeRegionSeasonId: payload || null,
  loading: false,
});

const reducerActions: $ReducerActions<'main'> = {
  [`${mainActionTypes.GET_REGATTAS}_FULFILLED`]: (
    state: $State,
    payload: Array<$Model<'Regatta'>>,
  ): $State => ({
    ...state,
    loading: false,
    regattas: payload,
  }),
  [`${mainActionTypes.GET_REGATTAS}_PENDING`]: actionStart,
  [`${mainActionTypes.GET_REGATTAS}_REJECTED`]: actionError,
  [`${mainActionTypes.GET_REGIONS}_FULFILLED`]: (
    state: $State,
    payload: Array<$FullRegion>,
  ): $State => ({
    ...state,
    loading: false,
    regions: payload,
  }),
  [`${mainActionTypes.GET_REGIONS}_PENDING`]: actionStart,
  [`${mainActionTypes.GET_REGIONS}_REJECTED`]: actionError,
  [`${mainActionTypes.SET_ACTIVE_REGATTA_ID}_FULFILLED`]: (
    state: $State,
    payload: { RegattaId: null | string },
  ): $State => ({
    ...state,
    activeRegattaId: payload.RegattaId || null,
    loading: false,
  }),
  [`${mainActionTypes.SET_ACTIVE_REGATTA_ID}_PENDING`]: actionStart,
  [`${mainActionTypes.SET_ACTIVE_REGATTA_ID}_REJECTED`]: actionError,
  [`${mainActionTypes.SET_ACTIVE_REGION_ID}_FULFILLED`]: actionSetActiveRegionId,
  [`${mainActionTypes.SET_ACTIVE_REGION_ID}_PENDING`]: actionStart,
  [`${mainActionTypes.SET_ACTIVE_REGION_ID}_REJECTED`]: actionError,
  [`${mainActionTypes.SET_ACTIVE_REGION_SEASON_ID}_FULFILLED`]: actionSetActiveRegionSeasonId,
  [`${mainActionTypes.SET_ACTIVE_REGION_SEASON_ID}_PENDING`]: actionStart,
  [`${mainActionTypes.SET_ACTIVE_REGION_SEASON_ID}_REJECTED`]: actionError,
  [`${mainActionTypes.SET_LANGUAGE}_FULFILLED`]: actionEnd,
  [`${mainActionTypes.SET_LANGUAGE}_PENDING`]: actionStart,
  [`${mainActionTypes.SET_LANGUAGE}_REJECTED`]: actionError,
  [`${mainActionTypes.SET_START}_FULFILLED`]: (
    state: $State,
    payload: moment.Moment | null,
  ): $State => ({
    ...state,
    start: payload || null,
  }),
  [`${mainActionTypes.SETUP_LANGUAGE}_FULFILLED`]: actionEnd,
  [`${mainActionTypes.SETUP_LANGUAGE}_PENDING`]: actionStart,
  [`${mainActionTypes.SETUP_LANGUAGE}_REJECTED`]: actionError,
  [`${mainActionTypes.SETUP_MAIN_DATA}_FULFILLED`]: actionEnd,
  [`${mainActionTypes.SETUP_MAIN_DATA}_PENDING`]: actionStart,
  [`${mainActionTypes.SETUP_MAIN_DATA}_REJECTED`]: actionError,
  [`${mainActionTypes.SETUP_MENU}_FULFILLED`]: (
    state: $State,
    payload: { menu: $Menu },
  ): $State => ({
    ...state,
    loading: false,
    menu: payload.menu,
  }),
  [`${mainActionTypes.SETUP_MENU}_PENDING`]: actionStart,
  [`${mainActionTypes.SETUP_MENU}_REJECTED`]: actionError,
  [`${mainActionTypes.SETUP_USER}_FULFILLED`]: actionEnd,
  [`${mainActionTypes.SETUP_USER}_PENDING`]: actionStart,
  [`${mainActionTypes.SETUP_USER}_REJECTED`]: actionError,
  [`${mainActionTypes.TOGGLE_REGION_SELECTOR}_FULFILLED`]: (
    state: $State,
    payload: boolean,
  ): $State => ({
    ...state,
    loading: false,
    showRegionSelector: payload,
  }),
  [`${mainActionTypes.TOGGLE_REGION_SELECTOR}_PENDING`]: actionStart,
  [`${mainActionTypes.TOGGLE_REGION_SELECTOR}_REJECTED`]: actionError,
};

export type $Reducer = (arg0: $State, arg1: $ActionEnvelope<$ReducerActions<'main'>>) => $State;

const reducer: $Reducer = (prevState, action) => {
  const state = prevState || initialState;

  const reducerAction = _.get(
    reducerActions,
    action.type,
    () => state,
  );

  return reducerAction(
    state,
    action.payload,
  );
};

export default reducer;
